import { resolve, type Source, SourceSchema } from '@/agent/refs';
import { getMapView } from '@/map/mapStore';
import {
  ensureFeatureLayer,
  requireLayerRecord,
  resolveFieldName,
  resolveFieldList,
  translateAttributes,
} from './shared/layers';
import type { Tool } from './types';

type StatsOp = 'count' | 'sum' | 'avg' | 'min' | 'max' | 'stddev' | 'var';
type ReturnMode = 'count' | 'stats' | 'list';

type Input = {
  source: Source | string;
  return_mode: ReturnMode;
  stats_field?: string;
  stats_op?: StatsOp;
  group_by?: string;
  out_fields?: string[];
  limit?: number;
  spatial_filter?: Source | string;
};

type FeatureSetSpec = {
  layer_id: string;
  where: string;
  order_by?: string;
  order_dir?: 'ASC' | 'DESC';
  limit?: number;
  in_current_extent?: boolean;
};

export const query: Tool<Input> = {
  name: 'query',
  description:
    'Aggregate query against a feature_set. Modes: "count" returns a number; "stats" runs an outStatistics op (with optional group_by); "list" returns rows. spatial_filter (any Source) restricts to features that intersect that geometry.',
  input_schema: {
    type: 'object',
    properties: {
      source: SourceSchema,
      return_mode: { enum: ['count', 'stats', 'list'] },
      stats_field: { type: 'string' },
      stats_op: {
        enum: ['count', 'sum', 'avg', 'min', 'max', 'stddev', 'var'],
      },
      group_by: { type: 'string' },
      out_fields: { type: 'array', items: { type: 'string' } },
      limit: { type: 'integer', minimum: 1, maximum: 1000 },
      spatial_filter: SourceSchema,
    },
    required: ['source', 'return_mode'],
    additionalProperties: false,
  },
  execute: async (input) => {
    const spec = await unwrapFeatureSet(input.source);
    const layer = await ensureFeatureLayer(spec.layer_id);
    const probe = requireLayerRecord(spec.layer_id).probe;
    const oidField = resolveFieldName(layer.objectIdField ?? 'objectid', probe, layer)
      ?? layer.objectIdField
      ?? 'objectid';
    const orderByField = resolveFieldName(spec.order_by, probe, layer);

    const explicitSpatial = input.spatial_filter
      ? await geometryFor(parseSource(input.spatial_filter))
      : undefined;
    const viewExtent = spec.in_current_extent ? getMapView()?.extent : undefined;
    if (spec.in_current_extent && !viewExtent) {
      throw new Error('query: in_current_extent set but the map view has no extent yet');
    }
    const spatialGeom = explicitSpatial ?? viewExtent;

    if (input.return_mode === 'count') {
      const count = await layer.queryFeatureCount({
        where: spec.where,
        geometry: spatialGeom,
        spatialRelationship: spatialGeom ? 'intersects' : undefined,
      });
      return { count };
    }

    if (input.return_mode === 'stats') {
      if (!input.stats_op) throw new Error('query(stats): stats_op is required');
      const statsField = resolveFieldName(input.stats_field, probe, layer);
      const groupByField = resolveFieldName(input.group_by, probe, layer);
      const onField = input.stats_op === 'count'
        ? statsField ?? oidField
        : statsField;
      if (!onField) throw new Error('query(stats): stats_field is required for non-count ops');
      if (input.group_by && !groupByField) {
        throw new Error(`query(stats): unknown group_by field "${input.group_by}"`);
      }
      const outStatistics = [
        {
          statisticType: input.stats_op,
          onStatisticField: onField,
          outStatisticFieldName: '_v',
        },
      ];
      const rs = await layer.queryFeatures({
        where: spec.where,
        outStatistics,
        groupByFieldsForStatistics: groupByField ? [groupByField] : undefined,
        geometry: spatialGeom,
        spatialRelationship: spatialGeom ? 'intersects' : undefined,
      });
      return {
        rows: rs.features.map((f) => translateAttributes(f.attributes ?? {}, probe)),
      };
    }

    // list
    const rs = await layer.queryFeatures({
      where: spec.where,
      outFields: resolveFieldList(input.out_fields, probe, layer) ?? ['*'],
      orderByFields: orderByField
        ? [`${orderByField} ${spec.order_dir ?? 'ASC'}`]
        : undefined,
      num: input.limit ?? Math.min(spec.limit ?? 50, 1000),
      returnGeometry: false,
      geometry: spatialGeom,
      spatialRelationship: spatialGeom ? 'intersects' : undefined,
    });
    return {
      rows: rs.features.map((f) => translateAttributes(f.attributes ?? {}, probe)),
      count: rs.features.length,
    };
  },
};

async function unwrapFeatureSet(sourceInput: Source | string): Promise<FeatureSetSpec> {
  const source = parseSource(sourceInput);

  // For a handle or a spatially-filtered feature_set, the resolved OIDs already encode
  // the full filter (where + spatial_filter + extent + limit). Query against that exact
  // OID set so a downstream count/stats/list reflects only the intended subset.
  if (source.kind === 'handle' || (source.kind === 'feature_set' && source.spatial_filter)) {
    const ref = await resolve(source);
    if (!ref.feature_set) {
      throw new Error('query: source did not resolve to a feature_set');
    }
    return materializeFeatureSet(ref.feature_set);
  }

  if (source.kind === 'feature_set') return source;
  throw new Error(`query: source must be feature_set or handle, got "${source.kind}"`);
}

async function materializeFeatureSet(
  fs: { layer_id: string; objectids: number[] },
): Promise<FeatureSetSpec> {
  if (fs.objectids.length === 0) return { layer_id: fs.layer_id, where: '1=0' };
  const layer = await ensureFeatureLayer(fs.layer_id);
  const oidField = layer.objectIdField || 'objectid';
  return { layer_id: fs.layer_id, where: `${oidField} IN (${fs.objectids.join(',')})` };
}

function parseSource(sourceInput: Source | string): Source {
  if (typeof sourceInput !== 'string') return sourceInput;
  try {
    const parsed = JSON.parse(sourceInput) as unknown;
    if (parsed && typeof parsed === 'object' && 'kind' in parsed) {
      return parsed as Source;
    }
  } catch {
    // fallthrough
  }
  throw new Error('query: invalid source JSON string');
}

async function geometryFor(source: Source): Promise<__esri.Geometry | undefined> {
  const ref = await resolve(source);
  return ref.geometry ?? ref.extent ?? undefined;
}
